import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { UserPlus, CheckCircle, Mail, Users } from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface ConsultantSetupProps {
  accessToken: string;
  onConsultantCreated?: () => void;
}

interface Company {
  id: string;
  name: string;
}

export function ConsultantSetup({ accessToken, onConsultantCreated }: ConsultantSetupProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [consultantType, setConsultantType] = useState<string>('');
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/companies`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      } else {
        setError('Failed to load companies');
      }
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('Failed to load companies');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const toggleCompany = (companyId: string) => {
    if (selectedCompanies.includes(companyId)) {
      setSelectedCompanies(selectedCompanies.filter(id => id !== companyId));
    } else {
      setSelectedCompanies([...selectedCompanies, companyId]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/create-consultant`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            email,
            name,
            assignedCompanies: selectedCompanies,
            type: consultantType && consultantType !== 'none' ? consultantType : undefined,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create consultant');
      }

      setSuccess(true);
      setSuccessMessage(data.message || 'Consultant created. They will receive an invitation email.');
      setEmail('');
      setName('');
      setConsultantType('');
      setSelectedCompanies([]);

      if (onConsultantCreated) {
        onConsultantCreated();
      }

    } catch (err: any) {
      console.error('Error creating consultant:', err);
      setError(err.message || 'Failed to create consultant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-blue-600" />
          <div>
            <h1>Create New Consultant</h1>
            <p className="text-muted-foreground">
              Add a consultant user who can manage multiple companies
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              {successMessage}
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Consultant Information</CardTitle>
            <CardDescription>
              Create a new consultant user and assign them to companies. Consultants can create additional companies and manage company administrators.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="consultant-name">Consultant Name *</Label>
                <Input
                  id="consultant-name"
                  type="text"
                  placeholder="Jane Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
                <p className="text-muted-foreground">
                  The full name of the consultant
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="consultant-email">Email Address *</Label>
                <Input
                  id="consultant-email"
                  type="email"
                  placeholder="consultant@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <p className="text-muted-foreground">
                  An invitation email will be sent to this address
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="consultant-type">Consultant Specialization (Optional)</Label>
                <Select
                  value={consultantType}
                  onValueChange={setConsultantType}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select specialization (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Generalist)</SelectItem>
                    <SelectItem value="bdm">Business Development Manager</SelectItem>
                    <SelectItem value="engagement-lead">Firm Engagement Lead</SelectItem>
                    <SelectItem value="functional">Functional Consultant</SelectItem>
                    <SelectItem value="technical-architect">Technical/Solution Architect</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground">
                  {consultantType === 'bdm' && 'Focuses on sales, intake, and client development'}
                  {consultantType === 'engagement-lead' && 'Owns client delivery and project management'}
                  {consultantType === 'functional' && 'Configures workflows, request types, and business processes'}
                  {consultantType === 'technical-architect' && 'Handles integrations and complex technical configuration'}
                  {(!consultantType || consultantType === 'none') && 'This consultant can perform any type of work'}
                </p>
              </div>

              <div className="border-t pt-6">
                <div className="space-y-2 mb-4">
                  <Label>Assign Companies</Label>
                  <p className="text-muted-foreground">
                    Select which companies this consultant can access. They will also be able to create new companies.
                  </p>
                </div>

                {loadingCompanies ? (
                  <div className="text-center py-4 text-muted-foreground">
                    Loading companies...
                  </div>
                ) : companies.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">
                    No companies available yet. The consultant can create companies once they log in.
                  </div>
                ) : (
                  <div className="border rounded-lg p-4 space-y-2 max-h-60 overflow-y-auto">
                    {companies.map((company) => (
                      <div key={company.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`company-${company.id}`}
                          checked={selectedCompanies.includes(company.id)}
                          onCheckedChange={() => toggleCompany(company.id)}
                        />
                        <Label 
                          htmlFor={`company-${company.id}`}
                          className="cursor-pointer flex-1"
                        >
                          {company.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-muted/50 border rounded-lg p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <h4>What happens next?</h4>
                    <ul className="text-muted-foreground space-y-1 mt-2">
                      <li>• A new consultant account will be created</li>
                      <li>• A confirmation email will be sent to their email address</li>
                      <li>• They must click the confirmation link to verify their email</li>
                      <li>• After email verification, they'll be prompted to set a password</li>
                      <li>• Once logged in, they can access assigned companies and create new ones</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" disabled={loading} className="flex-1">
                  <UserPlus className="mr-2 h-4 w-4" />
                  {loading ? 'Creating Consultant...' : 'Create Consultant & Send Invitation'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}